import React, { useCallback, useState } from "react"
import { useTranslations } from "@/hooks/useTranslations"
import { useDropzone } from "react-dropzone"
import { Button } from "@/components/ui/button"
import { X, Upload, File, FileText, Image as ImageIcon, FileSpreadsheet } from "lucide-react"
import { cn } from "@/lib/utils"

export interface UploadedFile {
	file: File
	id: string
	preview?: string
	type: "image" | "pdf" | "word" | "excel" | "other"
	storageUrl?: string
	storageId?: string
}

interface FileUploadProps {
	onFilesChange: (files: UploadedFile[]) => void
	maxFiles?: number
	maxSize?: number // in bytes
	acceptedTypes?: string[]
	className?: string
}

const DEFAULT_ACCEPTED_TYPES = [
	"image/*",
	"application/pdf",
	"application/msword",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
	"application/vnd.ms-excel",
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
]

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB

/** Hint categories, in the order they are listed to the user. */
const TYPE_HINTS: Array<{ key: string; matches: (mime: string) => boolean }> = [
	{ key: "typePdf", matches: (m) => m === "application/pdf" },
	{ key: "typeWord", matches: (m) => m.includes("word") || m.includes("wordprocessingml") },
	{ key: "typeExcel", matches: (m) => m.includes("excel") || m.includes("spreadsheetml") },
	{ key: "typeImages", matches: (m) => m.startsWith("image/") },
]

export function FileUpload({ onFilesChange, maxFiles = 3, maxSize = MAX_FILE_SIZE, acceptedTypes = DEFAULT_ACCEPTED_TYPES, className }: FileUploadProps) {
	const t = useTranslations("fileUpload")
	const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([])

	// The hint must describe what is actually accepted — it used to be a fixed
	// "PDF, Word, Excel, images" string even when the caller narrowed the types.
	const known = TYPE_HINTS.filter((h) => acceptedTypes.some(h.matches)).map((h) => t(h.key))
	const typesHint = (known.length > 0 ? known : acceptedTypes).join(", ")

	const getFileType = (file: File): UploadedFile["type"] => {
		if (file.type.startsWith("image/")) return "image"
		if (file.type === "application/pdf") return "pdf"
		if (file.type.includes("word") || file.type.includes("document")) return "word"
		if (file.type.includes("excel") || file.type.includes("sheet")) return "excel"
		return "other"
	}

	const getFileIcon = (type: UploadedFile["type"]) => {
		switch (type) {
			case "image":
				return <ImageIcon className='w-4 h-4' />
			case "pdf":
				return <FileText className='w-4 h-4' />
			case "word":
				return <FileText className='w-4 h-4' />
			case "excel":
				return <FileSpreadsheet className='w-4 h-4' />
			default:
				return <File className='w-4 h-4' />
		}
	}

	const onDrop = useCallback(
		(acceptedFiles: File[]) => {
			if (uploadedFiles.length + acceptedFiles.length > maxFiles) {
				alert(t("maxFilesError", { max: maxFiles }))
				return
			}

			const newFiles: UploadedFile[] = acceptedFiles.map((file) => {
				const fileType = getFileType(file)
				const uploadedFile: UploadedFile = {
					file,
					id: Math.random().toString(36).substr(2, 9),
					type: fileType,
				}

				// Create preview for images
				if (fileType === "image") {
					uploadedFile.preview = URL.createObjectURL(file)
				}

				return uploadedFile
			})

			const updatedFiles = [...uploadedFiles, ...newFiles]
			setUploadedFiles(updatedFiles)
			onFilesChange(updatedFiles)
		},
		[uploadedFiles, maxFiles, onFilesChange]
	)

	const removeFile = (fileId: string) => {
		const updatedFiles = uploadedFiles.filter((f) => {
			if (f.id === fileId && f.preview) {
				URL.revokeObjectURL(f.preview)
			}
			return f.id !== fileId
		})
		setUploadedFiles(updatedFiles)
		onFilesChange(updatedFiles)
	}

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		accept: acceptedTypes.reduce((acc, type) => {
			acc[type] = []
			return acc
		}, {} as Record<string, string[]>),
		maxSize,
		multiple: maxFiles > 1,
	})

	return (
		<div className={cn("space-y-4", className)}>
			{/* Upload Area - Only show if max files not reached */}
			{uploadedFiles.length < maxFiles && (
				<div
					{...getRootProps()}
					className={cn(
						"border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors",
						isDragActive ? "border-primary bg-primary/10" : "border-gray-300 hover:border-gray-400"
					)}
				>
					<input {...getInputProps()} />
					<Upload className='w-8 h-8 mx-auto mb-2 text-gray-400' />
					{isDragActive ? (
						<p className='text-primary'>{t("dropHere")}</p>
					) : (
						<div>
							<p className='text-gray-600 mb-1'>{t("dragOrClick")}</p>
							<p className='text-xs text-gray-500'>{t("acceptedTypesHint", { types: typesHint, size: Math.round(maxSize / 1024 / 1024) })}</p>
						</div>
					)}
				</div>
			)}

			{/* File List */}
			{uploadedFiles.length > 0 && (
				<div className='space-y-2'>
					<h4 className='text-sm font-medium text-gray-700'>
						{t("selectedFiles", { count: uploadedFiles.length, max: maxFiles })}
					</h4>
					<div className='space-y-2'>
						{uploadedFiles.map((uploadedFile) => (
							<div key={uploadedFile.id} className='flex items-center gap-3 p-3 bg-gray-50 rounded-lg'>
								{/* File Icon or Preview */}
								<div className='flex-shrink-0'>
									{uploadedFile.preview ? (
										<div className='w-10 h-10 relative overflow-hidden rounded'>
											<img src={uploadedFile.preview} alt={uploadedFile.file.name} className='absolute inset-0 w-full h-full object-cover' />
										</div>
									) : (
										<div className='w-10 h-10 bg-gray-200 rounded flex items-center justify-center'>{getFileIcon(uploadedFile.type)}</div>
									)}
								</div>

								{/* File Info */}
								<div className='flex-1 min-w-0'>
									<p className='text-sm font-medium text-gray-900 truncate'>{uploadedFile.file.name}</p>
									<p className='text-xs text-gray-500'>{(uploadedFile.file.size / 1024 / 1024).toFixed(2)} MB</p>
								</div>

								{/* Remove Button */}
								<Button variant='ghost' size='sm' onClick={() => removeFile(uploadedFile.id)} className='flex-shrink-0 h-8 w-8 p-0 text-gray-400 hover:text-red-500'>
									<X className='w-4 h-4' />
								</Button>
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	)
}
